import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { RaffleTrophy } from "./RaffleTrophy";

type Props = {
  prize: string;
  tokens: number;
  daysLeft: number;
  progressPercentage: number;
  onClick?: () => void;
};

const RaffleBanner: React.FC<Props> = ({
  prize,
  tokens,
  daysLeft,
  progressPercentage,
  onClick,
}) => {
  return (
    <BannerContainer onClick={onClick}>
      <InnerContainer>
        <PrizeText>
          {translate(STRINGS.RAFFLE_PRIZE)}: {prize}
        </PrizeText>
        <ProgressBarContainer>
          <EndDateText>{tokens}</EndDateText>
          <ProgressBar>
            <Progress progressPercentage={progressPercentage} />
            <IconCircle progressPercentage={progressPercentage}>
              <RaffleTrophy />
            </IconCircle>
          </ProgressBar>
          <EndDateText>{daysLeft}</EndDateText>
        </ProgressBarContainer>
        <ProgressBarHintsContainer>
          <HintText>{translate(STRINGS.TOKENS)}</HintText>
          <HintText>{translate(STRINGS.DAYS_LEFT).toLowerCase()}</HintText>
        </ProgressBarHintsContainer>
        <MessageText>{translate(STRINGS.MORE_SHIFTS_MORE_CHANCES)}</MessageText>
      </InnerContainer>
      <ShineEffect />
    </BannerContainer>
  );
};

export default RaffleBanner;

const BannerContainer = styled.div`
  padding: 4px;
  text-align: center;
  border-radius: 1rem;
  background: linear-gradient(
    to bottom right,
    var(--tw-purple-200),
    var(--tw-pink-200),
    var(--tw-indigo-300)
  );
  position: relative;
  overflow: hidden;
  z-index: 1;
`;

const InnerContainer = styled.div`
  background: rgba(255, 255, 255, 0.6);
  border-radius: calc(1rem - 4px);
  padding: 16px;
  color: var(--tw-indigo-900);
`;

const PrizeText = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  gap: 24px;
`;

const ProgressBar = styled.div`
  position: relative;
  flex-grow: 1;
  height: 10px;
  background-color: #fff;
  border-radius: 5px;
`;

const Progress = styled.div<{ progressPercentage: number }>`
  width: ${({ progressPercentage }) => progressPercentage}%;
  height: 100%;
  background-color: var(--tw-indigo-500);
  border-radius: 5px;
`;

const IconCircle = styled.div<{ progressPercentage: number }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  left: ${({ progressPercentage }) => progressPercentage}%;
  width: 18px;
  height: 18px;
  background-color: #fff;
  border-radius: 50%;
  border: 2px solid var(--tw-indigo-500);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const EndDateText = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

const MessageText = styled.div`
  font-size: 14px;
`;

const shine = keyframes`
  0% {
    opacity: 0.5;
    transform: translateX(-100%);
  }
  50% {
    opacity: 1;
    transform: translateX(100%);
  }
  100% {
    opacity: 0.5;
    transform: translateX(200%);
  }
`;

const ShineEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ${shine} 2s infinite;
  pointer-events: none;
`;

const ProgressBarHintsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
`;

const HintText = styled.div`
  font-size: 14px;
`;
